import type { Scene, SpeciesData, ClusterData } from '../types';
import { UmapScatter } from '../components/UmapScatter';
import { RadialChart  } from '../components/RadialChart';
import { clusterColorByName, clusterColorById, clusterIdByName } from '../clusterPalette';

export class Scene2 implements Scene {
  readonly panel: HTMLElement;
  private scatter!: UmapScatter;
  private radial!: RadialChart;
  private built = false;
  private selectedIdx = 0;

  constructor(
    root: HTMLElement,
    private readonly species: SpeciesData[],
    private readonly clusters: ClusterData[],
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = '2';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Species Signals</div>
        <div class="sm-panel-sub">
          UMAP · cosine metric · unit-hypersphere renormalised
        </div>
      </div>
      <div class="sm-panel-narrative">
        Each species carries a 64-dimensional habitat fingerprint derived from
        AlphaEarth embeddings. Species with similar fingerprints share
        ecological conditions — the signal that alerts us when those
        conditions shift.
      </div>
      <div class="sm-headline">
        Each species carries a unique 64-dimensional habitat fingerprint;
        similar fingerprints signal shared ecological conditions — and
        future change when those conditions shift.
      </div>
      <div class="sm-signals-body">
        <!-- Left: UMAP + most common list -->
        <div class="sm-col-left">
          <div class="sm-section-label" style="margin-top:0;">Feature Space</div>
          <div id="sm-umap-wrap"></div>
          <div class="sm-section-label">Most Common</div>
          <div id="sm-list-common"></div>
        </div>
        <!-- Right: radial chart + most distinct list -->
        <div class="sm-col-right">
          <div class="sm-section-label" style="margin-top:0;">Embedding Fingerprint</div>
          <div id="sm-radial-wrap"></div>
          <div id="sm-radial-label"
               style="font-size:9px;color:var(--c-grey);text-align:center;
                      margin-bottom:4px;letter-spacing:1px;">
            SELECT A SPECIES
          </div>
          <div class="sm-section-label">Most Distinct</div>
          <div id="sm-list-unique"></div>
        </div>
      </div>`;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (!this.built) {
      this.build();
      this.built = true;
    }
    this.radial.show(this.species[this.selectedIdx].embed);
  }

  onLeave(): void {
    this.radial?.stop();
  }

  // ── Build all sub-components ──────────────────────────────────────────────

  private build(): void {
    const umapWrap   = this.panel.querySelector<HTMLElement>('#sm-umap-wrap')!;
    const radialWrap = this.panel.querySelector<HTMLElement>('#sm-radial-wrap')!;

    // Color each species dot by its vegetation cluster
    const colorFn = (sp: SpeciesData) => clusterColorByName(sp.name) ?? '#97B8CB';

    this.scatter = new UmapScatter(
      umapWrap, this.species,
      (i) => this.select(i),
      colorFn,
      260, 270,
    );

    this.radial = new RadialChart(radialWrap, 270, 188);

    // Most common: top 10 by count (already sorted desc)
    this.buildList(
      'sm-list-common',
      this.species.slice(0, 10),
    );

    // Most distinct: lowest average cosine similarity to all others
    const sortedByDistinct = [...this.species].sort((a, b) => a.avg_sim - b.avg_sim);
    this.buildList('sm-list-unique', sortedByDistinct.slice(0, 8));

    // Auto-select #1 (most common)
    this.select(0);
  }

  private buildList(containerId: string, subset: SpeciesData[]): void {
    const el = this.panel.querySelector<HTMLElement>(`#${containerId}`)!;
    el.innerHTML = '';

    subset.forEach(d => {
      const i   = this.species.indexOf(d);
      const col = clusterColorByName(d.name) ?? '#97B8CB';

      const row = document.createElement('div');
      row.className  = 'sm-sp-row';
      row.dataset['i'] = String(i);

      row.innerHTML = `
        <div class="sm-sp-dot"
             style="background:${col};box-shadow:0 0 4px ${col};"></div>
        <div class="sm-sp-name">${d.name}</div>
        <div class="sm-sp-count">${d.count}</div>
        <div class="sm-sim-bar">
          <div class="sm-sim-fill" id="sf-${containerId}-${i}"
               style="background:${col};width:0%;"></div>
        </div>`;

      row.addEventListener('click', () => this.scatter.select(i));
      el.appendChild(row);
    });
  }

  private select(i: number): void {
    this.selectedIdx = i;
    const d = this.species[i];

    // Highlight list rows
    this.panel.querySelectorAll<HTMLElement>('.sm-sp-row').forEach(r => {
      r.classList.toggle('active', Number(r.dataset['i']) === i);
    });

    // Update similarity fill bars — dot product ∈ [-1, 1] → [0, 100]%
    ['sm-list-common', 'sm-list-unique'].forEach(lid => {
      this.panel.querySelectorAll<HTMLElement>(`#${lid} .sm-sp-row`).forEach(r => {
        const j   = Number(r.dataset['i']);
        const sim = d.sims[j];                       // cosine similarity
        const pct = Math.round(((sim + 1) / 2) * 100);
        const fill = this.panel.querySelector<HTMLElement>(`#sf-${lid}-${j}`);
        if (fill) {
          fill.style.width      = `${pct}%`;
          // warm colour for high similarity, sand for low
          fill.style.background = pct > 60
            ? (clusterColorByName(this.species[j].name) ?? '#97B8CB')
            : '#D0B39C';
        }
      });
    });

    // Update radial label
    const label = this.panel.querySelector<HTMLElement>('#sm-radial-label')!;
    label.textContent =
      `${d.name}  ·  avg sim = ${d.avg_sim.toFixed(3)}`;

    // Drive radial chart
    this.radial.show(d.embed);
  }
}
